/* ==========================================================================
   Sierra Sydney cart drawer behaviour
   Spec: CART-DRAWER-SAHARA-IMPLEMENTATION.md sections 4.2 and 5

   This does NOT replace Sahara's cart JavaScript. Sahara's CartItems /
   CartDrawerItems still own the change.js round trip and the Section
   Rendering API refresh, and the gift engine in snippets/cart-drawer.liquid
   still owns gift detection. This layer only adds what the design needs:

     1. A row is locked while its update is in flight, so a fast tapper
        cannot queue racing change.js calls.
     2. Interaction on a gift row is blocked outright (section 5: a change.js
        call on the gift makes the rule re-add it and the cart flickers).

   All behaviour is delegated from document, never bound to markup inside
   #CartDrawer-Body. That region is replaced wholesale by the Section
   Rendering API, and script tags in injected HTML do not execute, so any
   listener bound directly to it would be lost on the first cart change.

   Author: Latency Studio
   Version: 1.0
   ========================================================================== */

(function () {
  if (window.__ssCartDrawerLoaded) return;
  window.__ssCartDrawerLoaded = true;

  /* The gift engine in snippets/cart-drawer.liquid owns gift detection and
     already tags the row with this class. Reading its class rather than
     re-fetching /cart.js keeps one source of truth and avoids a second
     request on every cart change. */
  var GIFT_ROW_CLASS = 'ss-line--gift';

  /* ----------------------------------------------------------------------
     Quantity steppers
     Sahara's CartItems already debounces its own change handler, but the
     +/- buttons fire immediately. Locking the row is what actually stops a
     double tap producing two competing change.js calls.
     ---------------------------------------------------------------------- */

  var lockTimers = new WeakMap();

  function lockRow(row) {
    if (!row) return;
    row.classList.add('ss-is-busy');

    // The row is replaced by the section refresh on success. This timer is
    // only a safety net for a failed request that never re-renders.
    clearTimeout(lockTimers.get(row));
    lockTimers.set(
      row,
      setTimeout(function () {
        row.classList.remove('ss-is-busy');
      }, 5000)
    );
  }

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest(
        '#CartDrawer-Body .quantity__button, #CartDrawer-Body cart-remove-button'
      );
      if (!button) return;

      var row = button.closest('.cart-item');
      if (!row) return;

      // A gift line must never send a change.js call: the rule re-adds it and
      // the cart flickers. The controls are hidden in CSS, so this is a
      // belt-and-braces guard for the moment before the engine tags the row.
      if (row.classList.contains(GIFT_ROW_CLASS)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      lockRow(row);
    },
    true
  );

  document.addEventListener('change', function (event) {
    var input = event.target.closest('#CartDrawer-Body .quantity__input');
    if (!input) return;
    lockRow(input.closest('.cart-item'));
  });

  /* ----------------------------------------------------------------------
     Complete the Look

     Arrows scroll by one card width. Add posts to /cart/add.js with the
     `sections` parameter so the drawer re-renders in a single round trip
     (spec 4.3), showing "Added" for ~250ms first.
     ---------------------------------------------------------------------- */

  /* Recommendations are fetched, not rendered inline: the `recommendations`
     Liquid object is only populated on a request to
     product_recommendations_url. That URL re-renders this same section with
     the products filled in, and the track's markup is swapped in from it.
     Documented at:
     shopify.dev/docs/storefronts/themes/product-merchandising/recommendations */
  /* The last set of cards that rendered. The drawer section is replaced
     wholesale on every cart change, so the new shell arrives with an empty
     track and its own fetch still in flight. Painting the previous cards back
     immediately is what stops the row disappearing and reappearing — the spec
     wants Complete the Look present, not flickering. */
  var lastCards = '';

  function loadRecommendations() {
    var section = document.querySelector('[data-ss-ctl]');
    if (!section) return;

    var track = section.querySelector('[data-ss-ctl-track]');
    var url = section.dataset.ssCtlUrl;
    if (!track || !url) return;

    /* Cache first, network second.

       Every cart re-render hands back a freshly rendered section, and Liquid
       only populates `recommendations` on a request to the recommendations
       URL — so the new markup always arrives with an empty track and the
       ss-ctl--empty class, which is display:none. Painting the previous cards
       back synchronously is what stops the row vanishing and reappearing on
       every quantity tap. The network copy is reconciled below. */
    if (lastCards && (!track.innerHTML.trim() || section.classList.contains('ss-ctl--empty'))) {
      track.innerHTML = lastCards;
      section.classList.remove('ss-ctl--empty');
      setupLoop(track);
      updateArrows(track);
    }

    // Already showing the right cards for this seed product.
    if (section.dataset.ssCtlLoaded === url && track.innerHTML.trim()) {
      section.classList.remove('ss-ctl--empty');
      return;
    }

    section.dataset.ssCtlLoaded = url;

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var freshTrack = doc.querySelector('[data-ss-ctl-track]');
        // The section may have been replaced again while this was in flight.
        var liveSection = document.querySelector('[data-ss-ctl]');
        var liveTrack = liveSection && liveSection.querySelector('[data-ss-ctl-track]');
        if (!freshTrack || !liveTrack) return;

        var markup = freshTrack.innerHTML.trim();

        // Shopify returned nothing for this seed. Keep whatever is already on
        // screen rather than emptying a row that was populated a moment ago.
        if (!markup) {
          if (!liveTrack.innerHTML.trim()) {
            liveSection.classList.add('ss-ctl--empty');
          }
          return;
        }

        liveSection.classList.remove('ss-ctl--empty');

        /* Swap only if the recommendations actually changed.

           Compare against the last fetched markup, not against the live DOM:
           the DOM copy carries the loop's clones, so it never matches a fresh
           response and every re-render would look like a change. Re-writing
           innerHTML for an identical set resets the shopper's place in the row
           and rebuilds the clones for nothing — measured: the row jumped while
           the same eight cards were on screen. */
        if (markup === lastCards && liveTrack.querySelector('.ss-ctl__card')) {
          updateArrows(liveTrack);
          return;
        }

        lastCards = markup;
        liveTrack.innerHTML = markup;

        // Measure once the new cards have been laid out, and again after
        // their images arrive, since an image can change the row's width
        // (and so how many clones the loop needs to fill a viewport).
        requestAnimationFrame(function () {
          trimTrackOverhang(liveTrack);
          setupLoop(liveTrack);
          updateArrows(liveTrack);
        });

        liveTrack.querySelectorAll('img').forEach(function (img) {
          if (img.complete) return;
          img.addEventListener(
            'load',
            function () {
              trimTrackOverhang(liveTrack);
              setupLoop(liveTrack);
              updateArrows(liveTrack);
            },
            { once: true }
          );
        });
      })
      .catch(function () {
        // Network failure must not wipe cards that are already showing.
        var liveSection = document.querySelector('[data-ss-ctl]');
        var liveTrack = liveSection && liveSection.querySelector('[data-ss-ctl-track]');
        if (liveSection && liveTrack && !liveTrack.innerHTML.trim()) {
          liveSection.classList.add('ss-ctl--empty');
        }
      });
  }

  /* Distance from one card's start to the next — card width plus the track
     gap. Measured off two adjacent cards rather than adding a hardcoded gap
     to the card width: the track's gap is a CSS value that has been changed
     before, and a step that disagrees with it by even a few px accumulates
     over repeated arrow clicks until they no longer land on a card. */
  function cardStep(track) {
    var cards = track.querySelectorAll('.ss-ctl__card');
    if (!cards.length) return track.clientWidth;
    if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
    return cards[0].getBoundingClientRect().width;
  }

  // Cards belonging to the real (non-cloned) set, in source order.
  function realCards(track) {
    return Array.prototype.slice.call(
      track.querySelectorAll('.ss-ctl__card:not([data-ss-ctl-clone])')
    );
  }

  /* Remove any dead space after the final real card.

     The track's scrollable width can end up wider than the cards actually
     need — a trailing padding, a sub-pixel rounding, or a snap target the
     scroller cannot reach all leave the last card short of the right edge
     with a visible gap after it. Rather than guess at the cause in CSS, the
     real geometry is measured here and the difference is applied as a
     negative margin on the last card, which shortens scrollWidth by exactly
     the amount of the overhang.

     Only relevant when the loop has no clones (too few cards to wrap) — once
     clones are appended after the real last card there is always something
     to scroll into, so there is nothing to trim. */
  function trimTrackOverhang(track) {
    var cards = realCards(track);
    if (!cards.length) return;

    var last = cards[cards.length - 1];

    // Reset before measuring, or the previous correction is counted twice.
    last.style.marginInlineEnd = '';

    if (track.querySelector('[data-ss-ctl-clone]')) return;

    // Nothing to scroll: a single card that already fits needs no trim.
    if (track.scrollWidth <= track.clientWidth + 1) return;

    var trackRight = track.getBoundingClientRect().right;
    var maxScroll = track.scrollWidth - track.clientWidth;
    // Where the last card's right edge lands once scrolled to the end.
    var lastRightAtEnd =
      last.getBoundingClientRect().right - (maxScroll - track.scrollLeft);

    var overhang = Math.round(trackRight - lastRightAtEnd);
    if (overhang > 0) last.style.marginInlineEnd = -overhang + 'px';
  }

  /* ----------------------------------------------------------------------
     Seamless infinite loop

     The track is still a plain native scroller (overflow-x + scroll-snap) —
     no library. The loop is faked the same way most hand-rolled infinite
     carousels do it: clone enough cards onto each end to always fill the
     viewport, and when a scroll (from a drag, a wheel, or an arrow click)
     carries the view into the cloned region, silently re-point scrollLeft
     at the equivalent spot in the real set with scrolling suspended, so the
     jump is invisible.

     Needs re-running every time the track's markup changes: a fresh
     recommendations fetch, or a re-render after adding an item from the
     carousel (spec: the added product is filtered out and the shell
     re-renders with a brand new track).
     ---------------------------------------------------------------------- */

  function clearLoop(track) {
    track.querySelectorAll('[data-ss-ctl-clone]').forEach(function (clone) {
      clone.remove();
    });
    delete track.dataset.ssCtlLooped;
    delete track.dataset.ssCtlSpan;
    delete track.dataset.ssCtlRightBound;
    delete track.dataset.ssCtlLeftBound;
  }

  function setupLoop(track) {
    /* Rebuilding is destructive — it drops the clones, re-adds them, and
       resets scrollLeft to the first real card. Doing that when nothing has
       changed is what froze the carousel: cloning mutates the DOM inside
       <cart-drawer>, the MutationObserver that calls refreshCarousels sees
       that mutation, and the resulting rebuild yanked the track back to the
       start on every frame, so neither the arrows nor a swipe could move it.

       A signature of the inputs the layout depends on — which products, how
       many, and how wide the track is — tells a real change from an echo of
       our own clone writes. */
    var signature = realCards(track)
      .map(function (card) {
        var form = card.querySelector('[data-ss-upsell-form] [name="id"]');
        return form ? form.value : card.textContent.trim();
      })
      .join('|') + '@' + track.clientWidth;

    if (track.dataset.ssCtlSignature === signature) return;
    track.dataset.ssCtlSignature = signature;

    clearLoop(track);

    var cards = realCards(track);
    // Fewer than 2 cards can't loop — there is nothing to wrap around to.
    if (cards.length < 2) {
      cards.forEach(function (card) {
        card.classList.remove('ss-ctl__card--end');
      });
      if (cards.length) {
        cards[cards.length - 1].classList.add('ss-ctl__card--end');
      }
      return;
    }

    // Only the loop's own end-alignment matters once cloned; a bare CSS
    // `:last-child` rule would instead target the appended trailing clone.
    cards.forEach(function (card) {
      card.classList.remove('ss-ctl__card--end');
    });

    // Enough clones on each side to cover at least one full viewport, so the
    // wrap point is always reached before the clones run out.
    var step = cardStep(track);
    var perViewport = Math.max(1, Math.ceil(track.clientWidth / step)) + 1;
    var cloneCount = Math.min(cards.length, perViewport);

    var leadingClones = cards.slice(0, cloneCount).map(function (card) {
      var clone = card.cloneNode(true);
      clone.setAttribute('data-ss-ctl-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      return clone;
    });
    var trailingClones = cards.slice(-cloneCount).map(function (card) {
      var clone = card.cloneNode(true);
      clone.setAttribute('data-ss-ctl-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      return clone;
    });

    trailingClones.forEach(function (clone) {
      track.insertBefore(clone, cards[0]);
    });
    leadingClones.forEach(function (clone) {
      track.appendChild(clone);
    });

    track.dataset.ssCtlLooped = '1';

    // The distance between a real card and its own cloned copy at the far
    // end — i.e. one full lap of the repeating DOM pattern. Measured
    // directly off the first real card and its leading-clone copy rather
    // than inferred from card width and gap, since gap is themeable and
    // easy to get subtly wrong by a few px, which would make every
    // wraparound jump land off by that amount.
    var span = leadingClones[0].offsetLeft - cards[0].offsetLeft;
    track.dataset.ssCtlSpan = span;

    /* The window of scroll positions treated as "safe".

       It is deliberately NOT the real cards' range. Wrapping the moment the
       first clone came into view meant the track had to reach the very end
       of its scrollable width before correcting, so the shopper saw it stop
       dead and then snap — the opposite of a seamless loop.

       Instead the safe window is one clone-band in from each physical edge.
       The scroller therefore always has a full band of spare content on
       both sides to scroll into, and gets recentred long before it can run
       out. `scrollLeft` never reaches 0 or scrollWidth, so there is no hard
       end to hit in either direction. */
    var band = cloneCount * cardStep(track);
    track.dataset.ssCtlLeftBound = cards[0].offsetLeft - band / 2;
    track.dataset.ssCtlRightBound = cards[0].offsetLeft + span - band / 2;

    // Open on the first real card — with a full clone-band of content
    // already sitting on either side of it, so the very first swipe or
    // arrow click in either direction has somewhere to go.
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = cards[0].offsetLeft;
    track.style.scrollBehavior = '';
  }

  // While a wraparound jump is silently repositioning scrollLeft, ignore the
  // scroll events it fires so updateArrows and the loop check do not race it.
  var loopJumping = false;

  function checkLoopBoundary(track) {
    if (loopJumping) return;
    if (!track.dataset.ssCtlLooped) return;

    var span = Number(track.dataset.ssCtlSpan) || 0;
    var rightBound = Number(track.dataset.ssCtlRightBound);
    var leftBound = Number(track.dataset.ssCtlLeftBound);
    if (!span || isNaN(rightBound) || isNaN(leftBound)) return;

    // Shift by whole spans until back inside the real cards' range. A single
    // shift covers every normal case; the loop matters only when a very fast
    // throw crosses more than one span before a scroll event is delivered.
    var position = track.scrollLeft;
    var shift = 0;
    while (position + shift > rightBound) shift -= span;
    while (position + shift < leftBound) shift += span;
    if (!shift) return;

    // Preserve whatever a concurrent drag/momentum already set (both force
    // 'none'/'auto' for their own duration) rather than clobbering it — the
    // jump only needs snap and smooth-scroll out of the way for its own
    // single write, not to change what the caller left behind.
    var priorSnapType = track.style.scrollSnapType;
    var priorBehavior = track.style.scrollBehavior;

    loopJumping = true;
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    track.scrollLeft += shift;
    track.style.scrollBehavior = priorBehavior;
    track.style.scrollSnapType = priorSnapType;
    loopJumping = false;
  }

  function updateArrows(track) {
    var section = track.closest('[data-ss-ctl]');
    if (!section) return;
    var prev = section.querySelector('[data-ss-ctl-prev]');
    var next = section.querySelector('[data-ss-ctl-next]');

    // Looped tracks never run out of cards in either direction.
    if (track.dataset.ssCtlLooped) {
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
      return;
    }

    var max = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= max - 1;
  }

  /* When each track's in-flight smooth scroll is expected to be finished.
     Scroll events arriving before then belong to that animation and must
     not be corrected out from under it. */
  var smoothUntil = new WeakMap();
  var SMOOTH_MS = 600;

  document.addEventListener('click', function (event) {
    var arrow = event.target.closest('[data-ss-ctl-prev], [data-ss-ctl-next]');
    if (!arrow) return;

    var section = arrow.closest('[data-ss-ctl]');
    var track = section && section.querySelector('[data-ss-ctl-track]');
    if (!track) return;

    var dir = arrow.hasAttribute('data-ss-ctl-next') ? 1 : -1;

    /* Recentre before animating, never during.

       A smooth scroll is the browser's own animation; rewriting scrollLeft
       while it runs cancels the travel it still owed and drops the carousel
       between cards. Because the safe window leaves a whole clone-band of
       slack on each side, the step about to be taken always fits inside the
       content without wrapping — so correcting first, then animating, is
       both seamless and never interrupted. */
    checkLoopBoundary(track);
    smoothUntil.set(track, Date.now() + SMOOTH_MS);
    track.scrollBy({ left: dir * cardStep(track), behavior: 'smooth' });
  });

  /* Keep the arrows in step with the scroll position, and recentre the loop
     as soon as the track drifts out of its safe window.

     This fires on native touch scrolling too, which is the case with no
     JS-driven animation to protect: the finger is in charge, the safe
     window still has a clone-band of slack on both sides, and recentring
     immediately is what stops a fling ever reaching the scroller's physical
     end. Arrow clicks recentre before they animate instead (see above), so
     a smooth scroll is never cut short by this. */
  var settleTimers = new WeakMap();

  /* Belt and braces for the end of a gesture: a drag released without
     enough velocity to throw produces no further scroll events, so the
     final resting position gets one last check a beat later. */
  function settleLoop(track) {
    clearTimeout(settleTimers.get(track));
    settleTimers.set(
      track,
      setTimeout(function () {
        checkLoopBoundary(track);
      }, 140)
    );
  }

  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (!track || !track.matches || !track.matches('[data-ss-ctl-track]')) return;

      updateArrows(track);

      /* Recentre right away unless an arrow's smooth scroll is still
         playing — that is the one animation the browser owns and will
         abandon if scrollLeft moves underneath it. Everything else (a
         finger on a touch screen, a wheel, our own drag and throw) is
         corrected on the spot, so the track never drifts far enough to
         reach a physical edge and stop dead. */
      if (!smoothUntil.has(track) || Date.now() > smoothUntil.get(track)) {
        checkLoopBoundary(track);
      }

      settleLoop(track);
    },
    true
  );

  /* ----------------------------------------------------------------------
     Drag to swipe

     Touch already scrolls the track natively (overflow-x: auto), so this is
     for pointer devices: click and drag the row like a carousel.

     Touch pointers are ignored deliberately — hijacking them would replace
     the OS's momentum scrolling with something worse. A drag past a few
     pixels suppresses the click that follows, so dragging across a card
     never fires its Add button or opens the product.
     ---------------------------------------------------------------------- */

  var drag = null;
  /* 6px, not 4. The track sits inside a vertically scrolling drawer, so the
     bar for claiming a gesture as horizontal has to clear the wobble in a
     thumb that is actually starting a vertical scroll. */
  var DRAG_THRESHOLD = 6;
  // How far back the release velocity is measured. Long enough to smooth
  // out jitter, short enough that a pause before lifting reads as a stop.
  var VELOCITY_WINDOW_MS = 100;
  var momentum = null;

  function stopMomentum() {
    if (momentum) {
      cancelAnimationFrame(momentum);
      momentum = null;
    }
  }

  document.addEventListener('pointerdown', function (event) {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    var track = event.target.closest('[data-ss-ctl-track]');
    if (!track) return;

    stopMomentum();

    drag = {
      track: track,
      startX: event.clientX,
      startY: event.clientY,
      startScroll: track.scrollLeft,
      // Target scroll position, applied once per frame rather than on every
      // pointermove. Pointer events fire faster than the display refreshes,
      // so writing scrollLeft directly per event causes visible stutter.
      target: track.scrollLeft,
      moved: false,
      frame: null,
      // Recent pointer positions, for measuring the release velocity.
      samples: [{ x: event.clientX, time: event.timeStamp }],
      velocity: 0
    };
  });

  function applyScroll() {
    if (!drag) return;
    drag.frame = null;
    drag.track.scrollLeft = drag.target;

    /* Wrap mid-drag rather than at rest.

       A drag is the one case with no browser-owned animation to cut short,
       and it is also the case that would otherwise hit the scroller's hard
       physical end and stop dead against it. Wrapping here keeps the finger
       (or cursor) glued to the same card while the content underneath is
       silently rewound, so the row genuinely never runs out. The drag's own
       origin has to move by the same shift, or the next pointermove would
       compute its delta against a position that no longer exists. */
    var before = drag.track.scrollLeft;
    checkLoopBoundary(drag.track);
    var shifted = drag.track.scrollLeft - before;
    if (shifted) {
      drag.startScroll += shifted;
      drag.target += shifted;
    }
  }

  document.addEventListener(
    'pointermove',
    function (event) {
      if (!drag) return;

      var delta = event.clientX - drag.startX;
      var deltaY = event.clientY - drag.startY;

      /* Claim the gesture only once it is clearly horizontal.

         The track sits inside a vertically scrolling drawer, so a gesture
         that starts on a card may well be meant for the drawer. Comparing
         the two axes before committing means a mostly-vertical movement is
         left alone, and once either axis wins the decision sticks for the
         rest of the gesture rather than flickering between the two. */
      if (!drag.moved && !drag.rejected) {
        if (Math.abs(deltaY) > DRAG_THRESHOLD && Math.abs(deltaY) > Math.abs(delta)) {
          drag.rejected = true;
          return;
        }

        if (Math.abs(delta) > DRAG_THRESHOLD) {
          drag.moved = true;
        }
      }

      if (drag.rejected) return;

      if (drag.moved && !drag.started) {
        drag.started = true;
        drag.track.classList.add('is-dragging');
        // Snap and smooth-scroll both fight a direct drag, so they are
        // suspended for its duration and restored on release.
        drag.track.style.scrollSnapType = 'none';
        drag.track.style.scrollBehavior = 'auto';
        // Keeps the pointer bound to this element even outside its bounds.
        if (drag.track.setPointerCapture) {
          try {
            drag.track.setPointerCapture(event.pointerId);
            drag.pointerId = event.pointerId;
          } catch (e) {}
        }
      }

      if (!drag.moved) return;

      event.preventDefault();

      /* Velocity in px/ms, for the throw after release.

         Measured over a short rolling window rather than the last two
         events. A single pair is noisy, and — more importantly — it made a
         drag that paused before release still throw, because the final two
         samples could straddle the pause. Dropping samples older than the
         window means a finger that stops before lifting reads as
         stationary, which is what a shopper expects. */
      drag.samples.push({ x: event.clientX, time: event.timeStamp });
      while (
        drag.samples.length > 2 &&
        event.timeStamp - drag.samples[0].time > VELOCITY_WINDOW_MS
      ) {
        drag.samples.shift();
      }

      var oldest = drag.samples[0];
      var elapsed = event.timeStamp - oldest.time;
      drag.velocity = elapsed > 0 ? (event.clientX - oldest.x) / elapsed : 0;

      // A looped track is free to drag into its cloned region — the scroll
      // listener's checkLoopBoundary call wraps it invisibly — so only a
      // non-looping track (too few real cards) is clamped to its physical
      // scroll range.
      if (drag.track.dataset.ssCtlLooped) {
        drag.target = drag.startScroll - delta;
      } else {
        var max = drag.track.scrollWidth - drag.track.clientWidth;
        drag.target = Math.max(0, Math.min(max, drag.startScroll - delta));
      }

      // One write per frame.
      if (drag.frame === null) drag.frame = requestAnimationFrame(applyScroll);
    },
    // Explicitly non-passive: preventDefault is a no-op in a passive listener,
    // which browsers default to for move events on document.
    { passive: false }
  );

  /* The scrollLeft at which the card nearest a given position sits flush
     against the track's leading edge.

     `offsetLeft` is measured from the track's border box, so it carries the
     track's own offset plus its leading padding — the inset that lines the
     first card up with the rest of the drawer. scrollLeft is measured from
     the content edge instead, so the two differ by exactly that inset and
     snapping straight to `offsetLeft` parks every card that far off. The
     inset is derived from the first card rather than read out of CSS, so it
     stays right whatever --ss-pad is set to. */
  function cardScrollTarget(track, position) {
    var cards = track.querySelectorAll('.ss-ctl__card');
    if (!cards.length) return null;

    var inset = cards[0].offsetLeft;
    var best = null;
    var bestDist = Infinity;

    for (var i = 0; i < cards.length; i++) {
      var target = cards[i].offsetLeft - inset;
      var dist = Math.abs(target - position);
      if (dist < bestDist) {
        bestDist = dist;
        best = target;
      }
    }

    return best;
  }

  /* Glide the track to an exact resting place.

     CSS `proximity` snapping only pulls when the scroll happens to stop
     near a card, so a throw could settle with a card half out of frame.
     Easing to a chosen offset instead means every swipe finishes with a
     card aligned to the same left edge as the arrows produce. */
  function glideTo(track, destination) {
    var start = track.scrollLeft;
    var distance = destination - start;

    if (Math.abs(distance) < 1) {
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
      updateArrows(track);
      settleLoop(track);
      return;
    }

    var startTime = null;
    var duration = Math.min(460, Math.max(200, Math.abs(distance) * 1.1));

    function frame(now) {
      if (startTime === null) startTime = now;
      var progress = Math.min(1, (now - startTime) / duration);
      // easeOutCubic: quick to leave, gentle to arrive.
      var eased = 1 - Math.pow(1 - progress, 3);

      track.scrollLeft = start + distance * eased;

      if (progress < 1) {
        // Wrapping mid-glide would move the ground under the animation, so
        // the destination has to travel with it.
        var before = track.scrollLeft;
        checkLoopBoundary(track);
        var shifted = track.scrollLeft - before;
        if (shifted) start += shifted;

        updateArrows(track);
        momentum = requestAnimationFrame(frame);
        return;
      }

      momentum = null;
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
      checkLoopBoundary(track);
      updateArrows(track);
    }

    momentum = requestAnimationFrame(frame);
  }

  /* Decaying throw after release, so letting go mid-swipe glides to a stop
     instead of halting dead.

     Rather than let the decay run all the way down and hope CSS snapping
     tidies up, the resting point is projected from the current velocity and
     the nearest card to it is chosen up front. The throw then eases to that
     exact offset, so a flick always lands a card square in the frame — and
     a hard flick carries several cards, the way a carousel should feel.

     A looped track is not clamped to its physical [0, max] scroll range —
     that range includes the cloned cards, and clamping to it would stop a
     throw dead at the clone boundary instead of letting it glide through.
     Only a non-looping track (too few real cards) clamps. */
  function startMomentum(track, velocity) {
    var looped = !!track.dataset.ssCtlLooped;
    var max = track.scrollWidth - track.clientWidth;

    // Where a decaying throw would come to rest. The sum of a geometric
    // series with the 0.94 decay used previously, so the reach of a given
    // flick is unchanged — only the ending is tidier.
    var projected = track.scrollLeft - velocity * 16 * (1 / (1 - 0.94));

    if (!looped) projected = Math.max(0, Math.min(max, projected));

    var destination = cardScrollTarget(track, projected);
    if (destination === null) destination = projected;
    if (!looped) destination = Math.max(0, Math.min(max, destination));

    glideTo(track, destination);
  }

  function endDrag(event) {
    if (!drag) return;

    var track = drag.track;
    var moved = drag.moved;
    var velocity = drag.velocity;
    var pointerId = drag.pointerId;

    if (drag.frame !== null) cancelAnimationFrame(drag.frame);
    drag = null;

    if (pointerId !== undefined && track.releasePointerCapture) {
      try {
        track.releasePointerCapture(pointerId);
      } catch (e) {}
    }

    if (!moved) return;

    track.classList.remove('is-dragging');

    /* A flick throws; a slow drag settles onto whichever card it ended up
       nearest. Letting a slow release stop mid-card was the main thing that
       made the row feel loose — every gesture now finishes with a card
       aligned, exactly like an arrow click. */
    if (Math.abs(velocity) > 0.15) {
      startMomentum(track, velocity);
    } else {
      var destination = cardScrollTarget(track, track.scrollLeft);
      if (destination === null) {
        track.style.scrollSnapType = '';
        track.style.scrollBehavior = '';
        updateArrows(track);
        settleLoop(track);
      } else {
        glideTo(track, destination);
      }
    }

    // Swallow the click the browser fires immediately after a drag. The
    // listener is torn down on the next frame as well as on first use, so a
    // drag that produces no click cannot eat an unrelated later one.
    var swallow = function (clickEvent) {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      document.removeEventListener('click', swallow, true);
    };

    document.addEventListener('click', swallow, true);
    requestAnimationFrame(function () {
      document.removeEventListener('click', swallow, true);
    });
  }

  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  /* Shared add path.

     Both the direct add (one-variant product) and the size sheet go through
     this, so the free gift engine, the reward meter, the subtotal and the
     header count all update the same way whichever route the shopper took.
     Returns a promise so the sheet can close only once the cart really moved. */
  function addFromForm(form, button, extra) {
    var drawer = document.querySelector('cart-drawer');
    var body = new FormData(form);

    // Captured before the section HTML is swapped out.
    rememberCarouselScroll();

    // Ask for the same sections Sahara's own cart code refreshes, so the
    // drawer and the header count update in this one request.
    body.append('sections', 'cart-drawer,cart-counter');

    if (extra) {
      Object.keys(extra).forEach(function (key) {
        body.set(key, extra[key]);
      });
    }

    if (button) {
      button.disabled = true;
      button.dataset.ssLabel = button.textContent;
      button.textContent = 'Added';
      button.classList.add('is-added');
    }

    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: body
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) throw new Error(data.description || data.message);

        // Hold "Added" briefly so the confirmation is readable, then let
        // Sahara swap the section HTML back in.
        return new Promise(function (resolve) {
          setTimeout(function () {
            if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
              drawer.renderContents({ sections: data.sections }, false);
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ss-cart-upsells' });
            }
            resolve();
          }, 250);
        });
      })
      .catch(function (error) {
        // Restore the button so the shopper can retry.
        if (button) {
          button.disabled = false;
          button.classList.remove('is-added');
          if (button.dataset.ssLabel) button.textContent = button.dataset.ssLabel;
        }
        throw error;
      });
  }

  /* ----------------------------------------------------------------------
     Size picker sheet

     Add on a Complete the Look card used to post the first available variant
     straight to /cart/add, so a multi-size dress landed in the bag in a size
     nobody chose. Add now looks the product up and, when it has more than one
     variant, asks for the size first.
     ---------------------------------------------------------------------- */

  var qsCache = {};        // handle -> product JSON, for the drawer's lifetime
  var qsProduct = null;
  var qsSelection = {};    // option position (1-based) -> chosen value
  var qsOpener = null;     // the Add button that opened the sheet, for focus return

  function qsEl(sel) {
    var root = document.getElementById('ss-qs');
    return root ? root.querySelector(sel) : null;
  }

  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.theme && window.theme.settings && window.theme.settings.money_with_currency_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* The badge is a claim about the product, so it is driven by the product's
     own tags and nothing else. */
  function qsIsFinalSale(product) {
    return (product.tags || []).some(function (tag) {
      var t = String(tag).toLowerCase();
      return t === 'final sale' || t === 'no-returns';
    });
  }

  function qsVariantFor(product) {
    var names = (product.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
    if (!names.length) return product.variants[0];
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var match = true;
      for (var n = 0; n < names.length; n++) {
        if (qsSelection[n + 1] !== v.options[n]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  function qsFirstAvailable(product) {
    return product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
  }

  /* Is any variant in stock for this option value, given the other picks? */
  function qsValueAvailable(product, position, value) {
    return product.variants.some(function (v) {
      if (v.options[position - 1] !== value) return false;
      if (!v.available) return false;
      return Object.keys(qsSelection).every(function (pos) {
        return +pos === position || v.options[+pos - 1] === qsSelection[pos];
      });
    });
  }

  function qsRenderOptions() {
    var wrap = qsEl('[data-ss-qs-options]');
    if (!wrap || !qsProduct) return;
    wrap.textContent = '';

    (qsProduct.options || []).forEach(function (option, index) {
      var name = typeof option === 'string' ? option : option.name;
      var values = typeof option === 'string'
        ? qsProduct.variants.map(function (v) { return v.options[index]; }).filter(function (v, i, a) { return a.indexOf(v) === i; })
        : option.values;
      var position = index + 1;

      var group = document.createElement('div');

      var legend = document.createElement('p');
      legend.className = 'ss-qs__legend';
      var strong = document.createElement('b');
      strong.textContent = name;
      legend.appendChild(strong);
      if (qsSelection[position]) {
        var picked = document.createElement('span');
        picked.textContent = ': ' + qsSelection[position];
        legend.appendChild(picked);
      }
      group.appendChild(legend);

      var pills = document.createElement('div');
      pills.className = 'ss-qs__pills';
      pills.setAttribute('role', 'radiogroup');
      pills.setAttribute('aria-label', name);

      values.forEach(function (value) {
        var pill = document.createElement('button');
        pill.className = 'ss-qs__pill';
        pill.type = 'button';
        pill.setAttribute('role', 'radio');
        pill.dataset.ssQsPosition = String(position);
        pill.dataset.ssQsValue = value;
        pill.textContent = value;
        var chosen = qsSelection[position] === value;
        pill.setAttribute('aria-checked', chosen ? 'true' : 'false');
        if (!qsValueAvailable(qsProduct, position, value)) {
          pill.disabled = true;
          pill.setAttribute('aria-disabled', 'true');
        }
        pills.appendChild(pill);
      });

      group.appendChild(pills);
      wrap.appendChild(group);
    });
  }

  function qsSyncAddState() {
    var add = qsEl('[data-ss-qs-add]');
    var label = qsEl('[data-ss-qs-add-label]');
    var idField = qsEl('[data-ss-qs-id]');
    if (!add || !label || !qsProduct) return;

    var options = qsProduct.options || [];
    var unpicked = null;
    for (var i = 0; i < options.length; i++) {
      if (!qsSelection[i + 1]) {
        unpicked = typeof options[i] === 'string' ? options[i] : options[i].name;
        break;
      }
    }

    // Every variant out of stock: say so rather than inviting a dead tap.
    if (!qsProduct.variants.some(function (v) { return v.available; })) {
      add.disabled = true;
      label.textContent = 'Sold out';
      return;
    }

    if (unpicked) {
      add.disabled = true;
      label.textContent = 'Select a ' + unpicked;
      return;
    }

    var variant = qsVariantFor(qsProduct);
    if (!variant || !variant.available) {
      add.disabled = true;
      label.textContent = 'Unavailable';
      return;
    }

    idField.value = String(variant.id);
    add.disabled = false;
    label.textContent = 'Add to cart';
  }

  function qsSyncPrice() {
    var variant = qsVariantFor(qsProduct) || qsFirstAvailable(qsProduct);
    var priceEl = qsEl('[data-ss-qs-price]');
    var compareEl = qsEl('[data-ss-qs-compare]');
    if (!variant || !priceEl) return;
    priceEl.textContent = money(variant.price);
    var compare = variant.compare_at_price;
    if (compare && compare > variant.price) {
      compareEl.textContent = money(compare);
      compareEl.hidden = false;
    } else {
      compareEl.hidden = true;
    }
  }

  /* ----------------------------------------------------------------------
     Desktop panel content: gallery + accordions
     ---------------------------------------------------------------------- */

  var qsDesktop = function () {
    return window.matchMedia('(min-width: 768px)').matches;
  };

  var qsAccCache = {};   // handle -> [{title, html}]

  function qsSized(src, width) {
    // Shopify CDN URLs take &width=; keep any existing query intact.
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function qsRenderGallery(product) {
    var wrap = qsEl('[data-ss-qs-gallery]');
    if (!wrap) return;
    wrap.textContent = '';
    if (!qsDesktop()) return;

    var media = (product.media || []).filter(function (m) { return m.media_type === 'image'; });
    if (!media.length && product.images) {
      media = product.images.map(function (src) { return { src: src }; });
    }

    media.slice(0, 8).forEach(function (m, i) {
      var src = m.src || (m.preview_image && m.preview_image.src);
      if (!src) return;
      var img = document.createElement('img');
      img.src = qsSized(src, 750);
      img.srcset = qsSized(src, 500) + ' 500w, ' + qsSized(src, 750) + ' 750w, ' + qsSized(src, 1000) + ' 1000w';
      img.sizes = '494px';
      img.alt = m.alt || product.title || '';
      // The first image is what the shopper sees immediately; the rest are below the fold.
      img.loading = i === 0 ? 'eager' : 'lazy';
      wrap.appendChild(img);
    });
  }

  /* The accordion copy lives on the product page — some of it in metafields the
     product JSON does not carry — so it is read from the page itself rather than
     restated here. One source of truth: whatever the PDP shows, this shows.
     Fetched once per product, and only on desktop, where the rows are visible. */
  function qsLoadAccordions(product) {
    var handle = product.handle;
    if (qsAccCache[handle]) return Promise.resolve(qsAccCache[handle]);
    var url = product.url || ('/products/' + handle);
    return fetch(url.split('?')[0], { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('pdp fetch failed');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var rows = [];
        doc.querySelectorAll('.product__accordion details').forEach(function (d) {
          var summary = d.querySelector('summary');
          var body = d.querySelector('.accordion__body-inner') || d.querySelector('.accordion__body');
          if (!summary || !body) return;
          var title = summary.textContent.replace(/\s+/g, ' ').trim();
          var text = body.textContent.replace(/\s+/g, ' ').trim();
          // Never render an empty row.
          if (!title || !text) return;
          rows.push({ title: title, html: body.innerHTML });
        });
        qsAccCache[handle] = rows;
        return rows;
      })
      .catch(function () {
        qsAccCache[handle] = [];
        return [];
      });
  }

  var CHEVRON =
    '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
    '<path d="m3 5 4 4 4-4" stroke="currentColor" stroke-width="1.4"></path></svg>';

  function qsRenderAccordions(rows) {
    var wrap = qsEl('[data-ss-qs-acc]');
    if (!wrap) return;
    wrap.textContent = '';
    rows.forEach(function (row) {
      var details = document.createElement('details');
      var summary = document.createElement('summary');
      var label = document.createElement('span');
      label.textContent = row.title;
      summary.appendChild(label);
      var chev = document.createElement('span');
      chev.innerHTML = CHEVRON;
      summary.appendChild(chev.firstChild);
      details.appendChild(summary);
      var body = document.createElement('div');
      body.className = 'ss-qs__acc-body';
      // Same markup the PDP renders for this row.
      body.innerHTML = row.html;
      details.appendChild(body);
      wrap.appendChild(details);
    });
  }

  function qsOpen(product, opener) {
    var root = document.getElementById('ss-qs');
    if (!root) return;

    qsProduct = product;
    qsSelection = {};
    qsOpener = opener || null;

    var title = qsEl('[data-ss-qs-title]');
    title.textContent = product.title;
    title.href = product.url || '#';

    qsEl('[data-ss-qs-badge]').hidden = !qsIsFinalSale(product);
    var err = qsEl('[data-ss-qs-error]');
    err.hidden = true;
    err.textContent = '';

    qsRenderOptions();
    qsSyncPrice();
    qsSyncAddState();

    var more = qsEl('[data-ss-qs-more]');
    if (more) more.href = product.url || '#';

    qsRenderGallery(product);
    qsRenderAccordions([]);
    if (qsDesktop()) {
      qsLoadAccordions(product).then(function (rows) {
        // The shopper may have closed and reopened on another product meanwhile.
        if (qsProduct && qsProduct.handle === product.handle) qsRenderAccordions(rows);
      });
    }

    root.hidden = false;
    // Next frame so the transform transition actually runs.
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      /* Focus the sheet, not the first pill. Focusing a pill draws a ring that
         reads as an already-chosen size on touch, when nothing is picked yet. */
      var sheet = qsEl('[data-ss-qs-sheet]');
      (sheet || qsEl('[data-ss-qs-close]')).focus();
    });
  }

  function qsClose() {
    var root = document.getElementById('ss-qs');
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    var opener = qsOpener;
    qsOpener = null;
    setTimeout(function () {
      root.hidden = true;
      qsProduct = null;
      qsSelection = {};
      // Focus goes back where the shopper left it.
      if (opener && document.contains(opener)) opener.focus();
    }, 300);
  }

  function qsLoadProduct(handle) {
    if (qsCache[handle]) return Promise.resolve(qsCache[handle]);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        // The endpoint answers as text/javascript, so trust the status, not the
        // content type. An error page parsed as a product is worse than a throw.
        if (!r.ok) throw new Error('product lookup failed: ' + r.status);
        return r.json();
      })
      .then(function (product) {
        if (!product || !Array.isArray(product.variants) || !product.variants.length) {
          throw new Error('product lookup returned no variants');
        }
        qsCache[handle] = product;
        return product;
      });
  }

  /* Capture phase, so this decides before the plain add handler below sees the
     submit. A single-variant product still takes the original path. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target.closest('[data-ss-upsell-form]');
      if (!form) return;

      var card = form.closest('[data-ss-upsell]');
      var handle = card && card.dataset.ssHandle;
      if (!handle) return;   // no handle: fall through to the direct add

      /* Variant count comes from Liquid, so a one-variant product adds with no
         lookup at all. Only a product that actually needs a choice pays for the
         request. */
      var variantCount = parseInt(card.dataset.ssVariants, 10);
      if (variantCount === 1) return;   // let the plain add handler run

      var button = form.querySelector('[data-ss-upsell-add]');
      if (button && button.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      qsLoadProduct(handle)
        .then(function (product) {
          if (product.variants.length === 1) {
            return addFromForm(form, button, { id: String(product.variants[0].id) });
          }
          if (!product.url && card.dataset.ssUrl) product.url = card.dataset.ssUrl;
          qsOpen(product, button);
        })
        .catch(function () {
          /* Fail closed. Falling back to the form's own variant would add the
             first size again — the exact bug this sheet exists to fix — so the
             shopper is asked to retry instead. */
          if (!button) return;
          var original = button.dataset.ssLabel || button.textContent;
          button.dataset.ssLabel = original;
          button.textContent = 'Try again';
          setTimeout(function () {
            button.textContent = button.dataset.ssLabel || 'Add';
          }, 1600);
        });
    },
    true
  );

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-ss-upsell-form]');
    if (!form) return;
    event.preventDefault();
    var button = form.querySelector('[data-ss-upsell-add]');
    if (button && button.disabled) return;
    addFromForm(form, button).catch(function () {});
  });

  // Pick a size.
  document.addEventListener('click', function (event) {
    var pill = event.target.closest('.ss-qs__pill');
    if (pill) {
      if (pill.disabled) return;
      qsSelection[pill.dataset.ssQsPosition] = pill.dataset.ssQsValue;
      qsRenderOptions();
      qsSyncPrice();
      qsSyncAddState();
      return;
    }
    if (event.target.closest('[data-ss-qs-close]')) qsClose();
  });

  document.addEventListener('keydown', function (event) {
    var root = document.getElementById('ss-qs');
    if (!root || root.hidden) return;

    if (event.key === 'Escape') {
      qsClose();
      return;
    }

    // Keep focus inside the sheet while it is open.
    if (event.key !== 'Tab') return;
    var focusable = root.querySelectorAll(
      'a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Add to cart from the sheet.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-ss-qs-form]');
    if (!form) return;
    event.preventDefault();

    var add = form.querySelector('[data-ss-qs-add]');
    var label = form.querySelector('[data-ss-qs-add-label]');
    var err = form.querySelector('[data-ss-qs-error]');
    if (!add || add.disabled) return;

    var opener = qsOpener;
    add.disabled = true;
    label.textContent = 'Adding…';
    err.hidden = true;

    var addedId = form.querySelector('[data-ss-qs-id]').value;
    var addedHandle = qsProduct && qsProduct.handle;

    addFromForm(form, null)
      .then(function () {
        qsClose();

        /* The card that opened the sheet lived inside #CartDrawer-Body, which
           renderContents() has just replaced, so `opener` is detached. Flash the
           equivalent card in the fresh DOM instead. It may legitimately be gone:
           the upsell row skips products already in the cart, which is exactly
           what just happened. */
        var fresh = addedHandle &&
          document.querySelector('.ss-ctl__card[data-ss-handle="' + addedHandle + '"] [data-ss-upsell-add]');
        var flash = (fresh && document.contains(fresh)) ? fresh : (opener && document.contains(opener) ? opener : null);
        if (flash) {
          flash.dataset.ssLabel = flash.dataset.ssLabel || flash.textContent;
          flash.textContent = 'Added';
          flash.classList.add('is-added');
          setTimeout(function () {
            flash.textContent = flash.dataset.ssLabel || 'Add';
            flash.classList.remove('is-added');
          }, 900);
        }

        /* Scroll to the line that was just added rather than to the bottom of
           the list: Shopify does not always append, and on this store the new
           line arrives at the top. */
        var items = document.querySelector('cart-drawer-items');
        if (!items) return;
        var row = addedId && items.querySelector('.cart-item a[href*="variant=' + addedId + '"]');
        var target = row && row.closest('.cart-item');
        if (target) {
          target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else {
          items.scrollTo({ top: items.scrollHeight, behavior: 'smooth' });
        }
      })
      .catch(function (error) {
        // Sold out between the lookup and the add: keep the sheet open and say why.
        err.textContent = (error && error.message) || 'Sorry, that size just sold out.';
        err.hidden = false;
        add.disabled = false;
        label.textContent = 'Add to cart';
      });
  });


  /* ----------------------------------------------------------------------
     Clear stale busy locks after a re-render

     The Section Rendering API replaces the rows outright, so a lock set on
     the old node disappears with it. This only catches the case where the
     markup is patched in place and the class survives.
     ---------------------------------------------------------------------- */

  function clearLocks() {
    document
      .querySelectorAll('#CartDrawer-Body .cart-item.ss-is-busy')
      .forEach(function (row) {
        clearTimeout(lockTimers.get(row));
        row.classList.remove('ss-is-busy');
      });
  }

  /* The drawer section is re-rendered wholesale after an add, and the product
     just added is filtered out of the carousel. That means a brand new track
     element scrolled back to 0, so the row visibly jumps. Carrying the old
     scroll offset across keeps the shopper where they were. */
  var lastCarouselScroll = 0;

  function rememberCarouselScroll() {
    var track = document.querySelector('[data-ss-ctl-track]');
    if (track) lastCarouselScroll = track.scrollLeft;
  }

  function refreshCarousels() {
    document.querySelectorAll('[data-ss-ctl-track]').forEach(function (track) {
      // Trim first: it changes scrollWidth, which the clamp below depends on.
      trimTrackOverhang(track);
      // Rebuild the loop's clones before restoring scroll position — a
      // resize or a card added/removed changes cardStep and so how many
      // clones are needed, and the restore below depends on the resulting
      // scrollWidth being current.
      setupLoop(track);

      if (lastCarouselScroll && !track.dataset.ssRestored) {
        track.dataset.ssRestored = '1';
        // Clamped, because one fewer card means a shorter scroll range.
        var max = Math.max(0, track.scrollWidth - track.clientWidth);
        track.scrollLeft = Math.min(lastCarouselScroll, max);
      }
      updateArrows(track);
    });
  }

  /* ----------------------------------------------------------------------
     Reward bar

     The bar lives in #CartDrawer-Header, which Sahara re-renders through the
     Section Rendering API, so its markup is already correct after every cart
     change. What that does NOT give you is the fill animating: the element is
     replaced wholesale, so it appears at its new width instead of growing
     into it.

     Re-applying the width on the next frame lets the CSS transition run.
     ---------------------------------------------------------------------- */

  function animateShipBar() {
    var fill = document.querySelector('#CartDrawer-Header .ss-ship__fill');
    if (!fill) return;

    var target = fill.style.width;
    if (!target) return;

    // Start from the previous width so the transition has somewhere to go.
    var previous = fill.dataset.ssWidth;
    if (previous && previous !== target) {
      fill.style.transition = 'none';
      fill.style.width = previous;
      // Force layout so the browser registers the start value.
      void fill.offsetWidth;
      fill.style.transition = '';
    }

    requestAnimationFrame(function () {
      fill.style.width = target;
      fill.dataset.ssWidth = target;
    });
  }

  function onCartRendered() {
    clearLocks();
    refreshCarousels();
    animateShipBar();
    loadRecommendations();
  }

  /* onCartRendered writes to the DOM (classes, restored cards), which would
     re-trigger the observer that called it. Debouncing through one frame
     collapses a burst of mutations into a single pass and breaks that loop. */
  var renderTimer = null;

  function scheduleRender() {
    if (renderTimer !== null) return;
    renderTimer = requestAnimationFrame(function () {
      renderTimer = null;
      onCartRendered();
    });
  }

  function init() {
    /* Observe <cart-drawer>, not #CartDrawer-Body.

       Sahara's own cart code swaps innerHTML on the body, but the gift engine
       in cart-drawer.liquid calls replaceWith() on #CartDrawer-Header and
       #CartDrawer-Body instead. replaceWith destroys the node, taking any
       observer bound to it with it — so after a gift was added or removed
       nothing repainted the upsell row, and Complete the Look stayed missing
       until a manual refresh.

       <cart-drawer> is never replaced, so one observer here survives both
       refresh paths. */
    var drawerRoot = document.querySelector('cart-drawer');
    if (drawerRoot) {
      new MutationObserver(scheduleRender).observe(drawerRoot, {
        childList: true,
        subtree: true
      });
    }

    // The gift engine fires this after its own refresh. Belt and braces
    // alongside the observer above.
    document.addEventListener('ss:gift:rendered', scheduleRender);

    // Sahara publishes this after every successful cart update.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRender);
    }

    refreshCarousels();
    loadRecommendations();

    /* Explicit hook for the two code paths that rebuild the cart.

       The MutationObserver above already catches them, but it is indirect: it
       fires on any mutation inside <cart-drawer> and depends on the observed
       node surviving. renderContents() and the gift engine call this directly
       so the row's refresh does not rest on that inference alone. */
    window.ssCartDrawer = window.ssCartDrawer || {};
    window.ssCartDrawer.refreshUpsells = scheduleRender;

    // The drawer's --ss-pad and the card width both change at the 480px
    // breakpoint, so the overhang has to be measured again after a resize.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(refreshCarousels, 150);
    });

    // Seed the fill's start width so the first cart change animates.
    var fill = document.querySelector('#CartDrawer-Header .ss-ship__fill');
    if (fill && fill.style.width) fill.dataset.ssWidth = fill.style.width;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
